/**
 * B2BAccountPage — client account ledger (B2B portal)
 *
 * Accessible at /b2b/account/:clientId
 * Visible to agents (viewing any assigned client) and to the client themselves.
 *
 * Shows:
 *  - Summary bar: total outstanding, aging buckets
 *  - Open invoices tab: list of unpaid/partial invoices with "pay now" shortcut
 *  - Ledger tab: full transaction history with running balance
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Loader as Loader2, CircleAlert as AlertCircle, TrendingDown, Clock, TriangleAlert as AlertTriangle, TriangleAlert as AlertTriangle2, FileText, Receipt, CreditCard, ReceiptText, ChevronDown, ChevronUp, Calendar, Download } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useB2BUser } from './B2BUserContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AgingSummary {
  current_amount:    number;
  days_30:           number;
  days_60:           number;
  days_90_plus:      number;
  total_outstanding: number;
}

interface OpenInvoice {
  id:                 string;
  document_number:    string;
  doc_type_name:      string;
  doc_type_prefix:    string;
  issue_date:         string;
  total:              number;
  outstanding_amount: number;
  days_open:          number;
}

interface LedgerRow {
  id:                 string;
  document_number:    string;
  doc_type_name:      string;
  doc_type_prefix:    string;
  issue_date:         string;
  debit:              number;
  credit:             number;
  running_balance:    number;
  is_closed:          boolean;
  outstanding_amount: number | null;
}

type Tab = 'open' | 'ledger';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return `₪${Math.abs(n).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ageBadge(days: number) {
  if (days > 60) return 'bg-red-100 text-red-700';
  if (days > 30) return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-500';
}

const PREFIX_LABEL: Record<string, string> = {
  INV: 'חשבונית',
  RC:  'קבלה',
  DN:  'תעודת משלוח',
  SO:  'הזמנת מכירה',
  CN:  'זיכוי',
  IR:  'החזרה',
};

// ─── Aging Bar ────────────────────────────────────────────────────────────────

function AgingBar({ aging }: { aging: AgingSummary }) {
  const total = aging.total_outstanding;
  const pct   = (n: number) => total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0%';

  const buckets = [
    { label: 'עד 30 יום', amount: aging.current_amount, color: 'bg-emerald-400' },
    { label: '31–60 יום', amount: aging.days_30,        color: 'bg-amber-400'   },
    { label: '61–90 יום', amount: aging.days_60,        color: 'bg-orange-500'  },
    { label: '+90 יום',   amount: aging.days_90_plus,   color: 'bg-red-500'     },
  ];

  return (
    <div className="space-y-3">
      {/* Stacked bar */}
      <div className="flex h-3 w-full rounded-full overflow-hidden gap-0.5">
        {buckets.filter(b => b.amount > 0).map(b => (
          <div key={b.label}
               className={`${b.color} transition-all`}
               style={{ width: pct(b.amount) }}
               title={`${b.label}: ${fmt(b.amount)}`} />
        ))}
        {total === 0 && <div className="bg-gray-200 w-full" />}
      </div>
      {/* Legend */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {buckets.map(b => (
          <div key={b.label} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${b.color}`} />
            <div className="min-w-0">
              <p className="text-[10px] text-gray-400 truncate">{b.label}</p>
              <p className="text-xs font-semibold text-gray-700">{fmt(b.amount)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export function B2BAccountPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate     = useNavigate();
  const { session }  = useB2BUser();

  // Resolve company_id: prefer the specific client record matching the URL clientId,
  // then fall back to activeClientRecord (for client-role users).
  const companyId = useMemo(() => {
    if (!session || !clientId) return null;
    // Agent: find the client record that matches clientId in agentClientRecords
    const agentClientRecord = session.agentClientRecords?.find(r => r.id === clientId);
    if (agentClientRecord) return agentClientRecord.company_id;
    // Client role: use activeClientRecord if it matches, else search clientRecords
    if (session.activeClientRecord?.id === clientId) return session.activeClientRecord.company_id;
    const clientRecord = session.clientRecords?.find(r => r.id === clientId);
    if (clientRecord) return clientRecord.company_id;
    // Final fallback
    return session.activeClientRecord?.company_id ?? session.agentRecord?.company_id ?? null;
  }, [session, clientId]);

  const [tab,        setTab]        = useState<Tab>('open');
  const [aging,      setAging]      = useState<AgingSummary | null>(null);
  const [openInvs,   setOpenInvs]   = useState<OpenInvoice[]>([]);
  const [ledger,     setLedger]     = useState<LedgerRow[]>([]);
  const [clientName, setClientName] = useState<string>('');
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  // Date range for ledger
  const [fromDate,   setFromDate]   = useState('');
  const [toDate,     setToDate]     = useState('');
  const [expanded,   setExpanded]   = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!clientId || !companyId) return;

    // Fetch client name
    supabase
      .from('inventory_clients')
      .select('name')
      .eq('id', clientId)
      .maybeSingle()
      .then(({ data }) => { if (data) setClientName(data.name as string); });

    // Fetch aging + open invoices in parallel
    Promise.all([
      supabase.rpc('get_client_aging_summary', {
        p_company_id: companyId,
        p_client_id:  clientId,
      }),
      supabase.rpc('get_client_open_invoices', {
        p_company_id: companyId,
        p_client_id:  clientId,
      }),
    ]).then(([agingRes, invRes]) => {
      if (agingRes.error) { setError(agingRes.error.message); }
      else if (Array.isArray(agingRes.data) && agingRes.data.length > 0) {
        setAging(agingRes.data[0] as AgingSummary);
      } else {
        setAging({ current_amount: 0, days_30: 0, days_60: 0, days_90_plus: 0, total_outstanding: 0 });
      }
      if (invRes.error) { setError(e => e ?? invRes.error!.message); }
      else setOpenInvs((invRes.data ?? []) as OpenInvoice[]);
      setLoading(false);
    });
  }, [clientId, companyId]);

  // Ledger is fetched separately (lazy + date-filtered)
  const fetchLedger = () => {
    if (!clientId || !companyId) return;
    supabase.rpc('get_client_ledger', {
      p_company_id: companyId,
      p_client_id:  clientId,
      p_from_date:  fromDate || null,
      p_to_date:    toDate   || null,
    }).limit(10000).then(({ data, error: e }) => {
      if (e) setError(e.message);
      else setLedger((data ?? []) as LedgerRow[]);
    });
  };

  useEffect(() => {
    if (tab === 'ledger') fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const toggleRow = (id: string) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const balance = useMemo(
    () => ledger.length > 0 ? ledger[ledger.length - 1]!.running_balance : 0,
    [ledger],
  );

  if (!clientId || !companyId) return (
    <div className="flex items-center justify-center min-h-[60vh] gap-3 text-gray-400" dir="rtl">
      <AlertCircle className="w-5 h-5" />
      <span className="text-sm">פרטי לקוח חסרים</span>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" dir="rtl">

      {/* Back + title */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
          <ArrowRight className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">חשבון לקוח</h1>
          {clientName && <p className="text-sm text-gray-500">{clientName}</p>}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm">טוען נתונים…</span>
        </div>
      )}

      {!loading && aging && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2 sm:col-span-4 bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                  <TrendingDown className="w-3.5 h-3.5" />פיזור גיל חוב
                </p>
                <span className="text-lg font-bold text-gray-900">{fmt(aging.total_outstanding)}</span>
              </div>
              <AgingBar aging={aging} />
            </div>

            {/* Quick stat cards */}
            {[
              { label: 'עד 30 יום',  value: aging.current_amount, icon: Clock,          color: 'text-emerald-600 bg-emerald-50' },
              { label: '31–60 יום',  value: aging.days_30,        icon: AlertTriangle,  color: 'text-amber-600   bg-amber-50'   },
              { label: '61–90 יום',  value: aging.days_60,        icon: AlertTriangle2, color: 'text-orange-600  bg-orange-50'  },
              { label: '+90 יום',    value: aging.days_90_plus,   icon: AlertTriangle2, color: 'text-red-600     bg-red-50'     },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-2 ${color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <p className="text-[11px] text-gray-400">{label}</p>
                <p className="text-base font-bold text-gray-900">{fmt(value)}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
            {(['open', 'ledger'] as Tab[]).map(t => (
              <button key={t} onClick={() => setTab(t)}
                      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors
                        ${tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {t === 'open' ? `חשבוניות פתוחות (${openInvs.length})` : 'כרטסת'}
              </button>
            ))}
          </div>

          {/* ── Open invoices tab ─────────────────────────────────────────── */}
          {tab === 'open' && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {openInvs.length === 0 ? (
                <div className="flex flex-col items-center py-16 gap-3 text-gray-400">
                  <Receipt className="w-10 h-10 text-gray-200" />
                  <p className="text-sm font-medium text-gray-500">אין חשבוניות פתוחות</p>
                  <p className="text-xs">כל החשבוניות שולמו במלואן</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3 text-right">מסמך</th>
                      <th className="px-5 py-3 text-right hidden sm:table-cell">תאריך</th>
                      <th className="px-5 py-3 text-right">סכום מקורי</th>
                      <th className="px-5 py-3 text-right">יתרה פתוחה</th>
                      <th className="px-5 py-3 text-right hidden md:table-cell">ימים</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {openInvs.map(inv => (
                      <tr key={inv.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-300 shrink-0" />
                            <div>
                              <p className="font-mono font-bold text-gray-800 text-xs">{inv.document_number}</p>
                              <p className="text-[10px] text-gray-400">{PREFIX_LABEL[inv.doc_type_prefix] ?? inv.doc_type_name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell">
                          {new Date(inv.issue_date).toLocaleDateString('he-IL')}
                        </td>
                        <td className="px-5 py-3.5 font-mono text-gray-700">{fmt(inv.total)}</td>
                        <td className="px-5 py-3.5">
                          <span className="font-mono font-bold text-red-600">{fmt(inv.outstanding_amount)}</span>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${ageBadge(inv.days_open)}`}>
                            {inv.days_open}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td colSpan={2} className="px-5 py-3 text-xs font-semibold text-gray-500">
                        סה״כ ({openInvs.length} מסמכים)
                      </td>
                      <td className="px-5 py-3 font-mono font-semibold text-gray-700">
                        {fmt(openInvs.reduce((s, i) => s + i.total, 0))}
                      </td>
                      <td className="px-5 py-3 font-mono font-bold text-red-600">
                        {fmt(openInvs.reduce((s, i) => s + i.outstanding_amount, 0))}
                      </td>
                      <td className="hidden md:table-cell" />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}

          {/* ── Ledger tab ────────────────────────────────────────────────── */}
          {tab === 'ledger' && (
            <div className="space-y-3">
              {/* Date filter */}
              <div className="flex items-center gap-3 flex-wrap bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
                <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-400">מ-</label>
                  <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                         className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                         dir="ltr" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-400">עד-</label>
                  <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                         className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
                         dir="ltr" />
                </div>
                <button onClick={fetchLedger}
                        className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
                  רענן
                </button>
                <div className="flex-1" />
                <button onClick={() => {
                  const csv = [
                    ['מסמך','תאריך','חיוב','זיכוי','יתרה'],
                    ...ledger.map(r => [
                      r.document_number,
                      r.issue_date,
                      r.debit.toFixed(2),
                      r.credit.toFixed(2),
                      r.running_balance.toFixed(2),
                    ]),
                  ].map(row => row.join(',')).join('\n');
                  const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv' }));
                  const a = document.createElement('a'); a.href = url;
                  a.download = `כרטסת-${clientName || clientId}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors">
                  <Download className="w-3.5 h-3.5" />ייצוא CSV
                </button>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {ledger.length === 0 ? (
                  <div className="flex flex-col items-center py-16 gap-3 text-gray-400">
                    <ReceiptText className="w-10 h-10 text-gray-200" />
                    <p className="text-sm">אין תנועות בתקופה הנבחרת</p>
                  </div>
                ) : (
                  <>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          <th className="px-5 py-3 text-right">מסמך</th>
                          <th className="px-5 py-3 text-right hidden sm:table-cell">תאריך</th>
                          <th className="px-5 py-3 text-right">חיוב</th>
                          <th className="px-5 py-3 text-right">זיכוי</th>
                          <th className="px-5 py-3 text-right">יתרה</th>
                          <th className="px-5 py-3 w-8" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {ledger.map(row => (
                          <>
                            <tr key={row.id}
                                onClick={() => toggleRow(row.id)}
                                className="hover:bg-gray-50/60 transition-colors cursor-pointer">
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-2">
                                  {row.debit > 0
                                    ? <FileText  className="w-4 h-4 text-red-300 shrink-0" />
                                    : <CreditCard className="w-4 h-4 text-emerald-400 shrink-0" />}
                                  <div>
                                    <p className="font-mono font-bold text-gray-800 text-xs">{row.document_number}</p>
                                    <p className="text-[10px] text-gray-400">{PREFIX_LABEL[row.doc_type_prefix] ?? row.doc_type_name}</p>
                                  </div>
                                  {row.is_closed && (
                                    <span className="text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-semibold">
                                      סגור
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell">
                                {new Date(row.issue_date).toLocaleDateString('he-IL')}
                              </td>
                              <td className="px-5 py-3.5 font-mono">
                                {row.debit > 0
                                  ? <span className="text-red-600 font-semibold">{fmt(row.debit)}</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-5 py-3.5 font-mono">
                                {row.credit > 0
                                  ? <span className="text-emerald-600 font-semibold">{fmt(row.credit)}</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-5 py-3.5 font-mono font-bold">
                                <span className={row.running_balance > 0 ? 'text-red-600' : row.running_balance < 0 ? 'text-emerald-600' : 'text-gray-500'}>
                                  {row.running_balance < 0 && '('}
                                  {fmt(Math.abs(row.running_balance))}
                                  {row.running_balance < 0 && ')'}
                                </span>
                              </td>
                              <td className="px-3 py-3.5 text-gray-400">
                                {expanded[row.id]
                                  ? <ChevronUp   className="w-3.5 h-3.5" />
                                  : <ChevronDown className="w-3.5 h-3.5" />}
                              </td>
                            </tr>
                            {expanded[row.id] && (
                              <tr key={`${row.id}-exp`} className="bg-gray-50/50">
                                <td colSpan={6} className="px-5 py-3">
                                  <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                                    <span>תאריך: <strong>{new Date(row.issue_date).toLocaleDateString('he-IL')}</strong></span>
                                    {row.outstanding_amount !== null && row.outstanding_amount > 0 && (
                                      <span className="text-red-600">יתרה פתוחה: <strong>{fmt(row.outstanding_amount)}</strong></span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded-full font-semibold ${row.is_closed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                      {row.is_closed ? 'שולם במלואו' : 'טרם שולם'}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 border-t-2 border-gray-200">
                          <td colSpan={4} className="px-5 py-3 text-xs font-semibold text-gray-500">
                            יתרה סופית ({ledger.length} תנועות)
                          </td>
                          <td className={`px-5 py-3 font-mono font-bold text-base ${balance > 0 ? 'text-red-600' : balance < 0 ? 'text-emerald-600' : 'text-gray-500'}`}>
                            {fmt(Math.abs(balance))}
                            {balance < 0 && ' (זכות)'}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
